import { consentKeys, fetchConsentById, resolveConsentReadLocale } from "@/features/consent/api/selectConsentById";
import { isConsentSignedByUser } from "@/features/consent/utils/consentGating";
import { getConsentSigningRoute, type ConsentRouteContext } from "@/features/consent/utils/consentRoutes";
import type { ConsentViewT } from "@/types/database/consents";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

interface LoadConsentCompletePageInput {
    queryClient: QueryClient;
    consentId: string;
    diaryId: string;
    userId?: string;
    routeContext: ConsentRouteContext;
    invalidRedirect: { to: string; params: { diaryId: string } };
}

export async function loadConsentCompletePage({
    queryClient,
    consentId,
    diaryId,
    userId,
    routeContext,
    invalidRedirect,
}: LoadConsentCompletePageInput) {
    const locale = resolveConsentReadLocale();
    const cachedConsent = queryClient.getQueryData<ConsentViewT | null>(consentKeys.byId(consentId, locale));
    const cachedIsSigned = Boolean(userId && cachedConsent && isConsentSignedByUser(cachedConsent, userId));

    const consent =
        cachedConsent && cachedConsent.diary_id === diaryId && cachedIsSigned
            ? cachedConsent
            : await queryClient.fetchQuery({
                  queryKey: consentKeys.byId(consentId, locale),
                  queryFn: () => fetchConsentById(consentId, locale),
                  staleTime: cachedIsSigned ? undefined : 0,
              });

    if (!consent || consent.diary_id !== diaryId) {
        throw redirect(invalidRedirect);
    }

    if (userId && !isConsentSignedByUser(consent, userId)) {
        throw redirect(getConsentSigningRoute(diaryId, consentId, routeContext));
    }

    return { consentId };
}
